import type { Annotation, ContentPackage, VocabularyItem } from './types';
import { sourceLabel } from './content';
import { downloadBlob, downloadText } from './storage';

function dateStamp() {
  return new Date().toISOString().slice(0, 10);
}

function csvCell(value: string): string {
  const raw = String(value ?? '');
  const safe = /^[\s]*[=+@-]/.test(raw) ? `'${raw}` : raw;
  return `"${safe.replaceAll('"', '""')}"`;
}

export function escapeMarkdown(value:string):string { return value.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/([\\`*_{}\[\]()#+.!|~-])/g,'\\$1'); }

export interface ExportOptions { translation:boolean;comment:boolean;tags:boolean;source:boolean }
export const defaultExportOptions:ExportOptions={translation:true,comment:true,tags:true,source:true};
export function editorialExportNotes(annotation:Annotation,content:ContentPackage,options=defaultExportOptions):string[]{
 const parts=annotation.anchor.fragments??[annotation.anchor];
 const notes=parts.flatMap(anchor=>{
  const block=content.blocks?.find(b=>b.id===anchor.blockId);
  if(!block?.editorialNoteRu)return [];
  const matches=anchor.language==='ru'?block.ruText.slice(anchor.start,anchor.end)===anchor.exactQuote:options.translation&&annotation.quoteRu===block.ruText;
  return matches?[block.editorialNoteRu]:[];
 });
 return [...new Set(notes)];
}
export function buildMarkdown(annotations: Annotation[], content: ContentPackage, options=defaultExportOptions): string {
  const lines = [
    `# ${content.document.titleEn}`,
    '',
    `Экспорт заметок · ${dateStamp()}`,
    '',
    '> Русский слой — рабочий учебный перевод. Для спорных формулировок сверяйтесь с PDF.',
    '',
  ];
  for (const annotation of annotations.filter((item) => !item.deletedAt)) {
    lines.push(`## ${annotation.category === 'important' ? 'Важное' : annotation.category === 'apply' ? 'Применить' : 'Вопрос'}`);
    lines.push('');
    lines.push(`> ${escapeMarkdown(annotation.anchor.exactQuote).replaceAll('\n', '\n> ')}`);
    if (options.translation && annotation.quoteRu && annotation.anchor.language==='en') lines.push('', `**Перевод контекста:** ${escapeMarkdown(annotation.quoteRu).replaceAll('\n', ' ')}`);
    for(const note of editorialExportNotes(annotation,content,options))lines.push('',`Примечание к переводу: ${escapeMarkdown(note)}`);
    if (options.comment && annotation.comment) lines.push('', `**Комментарий:** ${escapeMarkdown(annotation.comment)}`);
    if (options.tags && annotation.tags.length) lines.push('',`Теги: ${escapeMarkdown(annotation.tags.join(', '))}`);
    if (options.source) lines.push('', `Источник: ${annotation.sourceRefs.map((ref) => `PDF page ${ref.pdfPageNumber}`).join(', ')} · blockId: \`${annotation.anchor.blockId}\``, '');
  }
  return `${lines.join('\n').trim()}\n`;
}

export function buildAnkiCsv(items: VocabularyItem[]): string {
  const lines = ['Front,Back,Context,Source,Tags'];
  for (const item of items.filter((value) => !value.archivedAt && value.meaningRu.trim())) {
    lines.push([
      csvCell(item.expression),
      csvCell(item.meaningRu),
      csvCell(item.contextQuote),
      csvCell(item.anchor.blockId),
      csvCell('npt27'),
    ].join(','));
  }
  return `${lines.join('\n')}\n`;
}

export async function buildDocx(annotations: Annotation[], content: ContentPackage, options=defaultExportOptions): Promise<Blob> {
  if(typeof document!=='undefined'&&typeof Worker!=='undefined'){
    return new Promise<Blob>((resolve,reject)=>{const worker=new Worker(new URL('./export-worker.ts',import.meta.url),{type:'module'});worker.onmessage=e=>{worker.terminate();e.data.error?reject(new Error(e.data.error)):resolve(e.data.blob);};worker.onerror=e=>{worker.terminate();reject(new Error(e.message));};const ids=new Set(annotations.flatMap(a=>(a.anchor.fragments??[a.anchor]).map(f=>f.blockId)));worker.postMessage({annotations,content:{document:content.document,blocks:content.blocks.filter(b=>ids.has(b.id)).map(({id,ruText,editorialNoteRu})=>({id,ruText,editorialNoteRu}))},options});});
  }
  const { Document, HeadingLevel, Packer, Paragraph, TextRun }=await import('docx');
  const runs=(text:string,italics=false)=>text.split(/\r?\n/).map((line,i)=>new TextRun({text:line,italics,...(i?{break:1}:{})}));
  const children = [
    new Paragraph({ text: content.document.titleEn, heading: HeadingLevel.TITLE }),
    new Paragraph({ text: `Экспорт заметок · ${dateStamp()}` }),
    new Paragraph({ text: 'Русский слой — рабочий учебный перевод. Для спорных формулировок сверяйтесь с PDF.' }),
  ];
  for (const annotation of annotations.filter((item) => !item.deletedAt)) {
    children.push(new Paragraph({ text: annotation.category === 'important' ? 'Важное' : annotation.category === 'apply' ? 'Применить' : 'Вопрос', heading: HeadingLevel.HEADING_1 }));
    children.push(new Paragraph({ children: runs(annotation.anchor.exactQuote,true) }));
    if (options.translation && annotation.quoteRu && annotation.anchor.language==='en') children.push(new Paragraph({ children: runs(`Перевод контекста: ${annotation.quoteRu}`) }));
    for(const note of editorialExportNotes(annotation,content,options))children.push(new Paragraph({text:`Примечание к переводу: ${note}`}));
    if (options.comment && annotation.comment) children.push(new Paragraph({ children: runs(`Комментарий: ${annotation.comment}`) }));
    if (options.tags && annotation.tags.length) children.push(new Paragraph({text:`Теги: ${annotation.tags.join(', ')}`}));
    if (options.source) children.push(new Paragraph({ text: `Источник: ${annotation.sourceRefs.map((ref) => `PDF page ${ref.pdfPageNumber}`).join(', ')} · blockId: ${annotation.anchor.blockId}` }));
  }
  return Packer.toBlob(new Document({ sections: [{ children }] }));
}

export function exportMarkdown(annotations: Annotation[], content: ContentPackage) {
  downloadText(`npt27-notes-${dateStamp()}.md`, buildMarkdown(annotations, content), 'text/markdown;charset=utf-8');
}

export function exportAnki(items: VocabularyItem[]) {
  downloadText(`npt27-words-${dateStamp()}.csv`, buildAnkiCsv(items), 'text/csv;charset=utf-8');
}

export async function exportWord(annotations: Annotation[], content: ContentPackage) {
  downloadBlob(`npt27-notes-${dateStamp()}.docx`, await buildDocx(annotations, content));
}

export function annotationSource(annotation: Annotation) {
  return annotation.sourceRefs.map((ref) => `PDF page ${ref.pdfPageNumber}`).join(', ');
}
